/**
 * Studio detail — everything about one studio: owner contact + device,
 * full profile (address, tax, bank), subscription + billing channel,
 * team, projects, billing history (store + web), and the admin plan
 * override / Razorpay refund tools.
 *
 * Data:
 *   - `adminGetStudio` (callable) — joined profile / team / projects /
 *     billing history.
 *   - Live snapshots of the org doc + `payments` subcollection for the
 *     override form and refund table (instant refresh after an action).
 */
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Grid,
  Group,
  Image,
  Modal,
  NumberInput,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandWhatsapp,
  IconBuildingBank,
  IconExternalLink,
  IconFolder,
  IconLock,
  IconMail,
  IconMapPin,
  IconPhone,
  IconPlayerPause,
  IconReceipt,
  IconReceiptRefund,
  IconSettings,
  IconShieldHalfFilled,
  IconUser,
  IconUsers,
  IconWorldWww,
} from '@tabler/icons-react';
import { collection, doc, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  ChannelBadge,
  CopyText,
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  PlatformBadge,
  RefreshButton,
  RelTime,
  SectionCard,
  StatusBadge,
  StorageBar,
  StudioAvatar,
  TierBadge,
  useAdminData,
} from '@/components/ui';
import { RecalculateStorageButton } from '@/components/RecalculateStorageButton';
import type { StudioDetail, TeamMember } from '@/lib/adminTypes';
import { callAdmin, db } from '@/lib/firebase';
import {
  EVENT_LABEL,
  ROLE_LABEL,
  STORAGE_KIND_LABEL,
  STORE_LABEL,
  TIER_LABEL,
  fmtBytes,
  fmtDate,
  fmtDateTime,
  fmtInr,
  fmtMoney,
  fmtPhone,
  label,
  waLink,
} from '@/lib/format';

type Tier = 'free' | 'solo' | 'studio' | 'studio_plus' | 'agency';

type OrgDoc = {
  subscription?: {
    tier?: string;
    expiresAt?: { toMillis: () => number };
    source?: string;
    manualOverrideNote?: string;
  };
};

// Shape of a row in `organizations/{orgId}/payments/{paymentId}` as
// written by the Razorpay webhook. `refund` is set by
// `adminRefundRazorpayPayment`.
type PaymentRow = {
  id: string;
  paymentId?: string;
  amount?: number; // paise
  status?: string;
  tier?: string;
  period?: string;
  capturedAt?: { toMillis: () => number };
  invoiceUrl?: string;
  refund?: {
    amount?: number; // paise
    refundedAt?: { toMillis: () => number };
  };
};

export function OrgDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { data, error, loading, reload } = useAdminData(
    () => callAdmin.getStudio({ orgId: id ?? '' }),
    [id],
  );
  const [tab, setTab] = useState<string | null>('overview');

  if (error && !data) {
    return (
      <Stack>
        <BackLink />
        <ErrorBlock message={error} onRetry={reload} />
      </Stack>
    );
  }
  if (!data || !id) return <LoadingBlock label="Loading studio…" />;

  const s = data.studio;
  const members = data.team.filter((m) => m.role !== 'client');
  const clients = data.team.filter((m) => m.role === 'client');

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <BackLink />
        <RefreshButton onClick={reload} loading={loading} />
      </Group>

      <Card withBorder radius="lg" p={0} style={{ overflow: 'hidden' }}>
        {data.profile.coverPhotoUrl ? (
          <Image src={data.profile.coverPhotoUrl} h={120} fit="cover" alt="" />
        ) : (
          <Box h={56} style={{ background: 'linear-gradient(90deg, #0A4580, #2172C0)' }} />
        )}
        <Box p="lg">
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
            <Group gap="md" wrap="nowrap" align="flex-start">
              <StudioAvatar name={s.name} logoUrl={s.logoUrl} size={64} />
              <Stack gap={4}>
                <Text fw={750} style={{ fontSize: 24, letterSpacing: -0.4, lineHeight: 1.2 }}>
                  {s.name}
                </Text>
                {data.profile.tagline ? (
                  <Text c="dimmed" size="sm">
                    {data.profile.tagline}
                  </Text>
                ) : null}
                <Group gap={6} mt={4}>
                  <TierBadge tier={s.effectiveTier} />
                  <StatusBadge status={s.status} />
                  <ChannelBadge channel={s.channel} sandbox={s.environment === 'SANDBOX'} />
                </Group>
              </Stack>
            </Group>
            <Stack gap={4} align="flex-end">
              <CopyText value={s.id} />
              <Text size="xs" c="dimmed">
                Created {fmtDate(s.createdAt)}
                {s.createdPlatform !== 'unknown' ? ' from ' : ''}
              </Text>
              {s.createdPlatform !== 'unknown' ? <PlatformBadge platform={s.createdPlatform} /> : null}
              <Text size="xs" c="dimmed">
                Last active <RelTime ms={s.lastActiveAt} />
              </Text>
            </Stack>
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="md" mt="lg">
            <MiniStat label="Members" value={s.memberCount} />
            <MiniStat label="Clients" value={s.clientCount} />
            <MiniStat label="Projects" value={s.projectCount} />
            <MiniStat
              label="Storage"
              value={s.storageLimitBytes ? `${fmtBytes(s.storageBytes)} / ${fmtBytes(s.storageLimitBytes)}` : fmtBytes(s.storageBytes)}
            />
            <MiniStat label="Profile complete" value={`${s.profileScore}%`} />
          </SimpleGrid>
        </Box>
      </Card>

      <Tabs value={tab} onChange={setTab} variant="pills" radius="md">
        <Tabs.List mb="md">
          <Tabs.Tab value="overview" leftSection={<IconUser size={15} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="team" leftSection={<IconUsers size={15} />}>
            Team ({data.team.length})
          </Tabs.Tab>
          <Tabs.Tab value="projects" leftSection={<IconFolder size={15} />}>
            Projects ({data.projects.length})
          </Tabs.Tab>
          <Tabs.Tab value="billing" leftSection={<IconReceipt size={15} />}>
            Billing ({data.billing.length})
          </Tabs.Tab>
          <Tabs.Tab value="plan" leftSection={<IconSettings size={15} />}>
            Manage plan
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview">
          <OverviewTab data={data} onOpenTeam={() => setTab('team')} onRecalculated={reload} />
        </Tabs.Panel>
        <Tabs.Panel value="team">
          <Stack gap="lg">
            <SectionCard title="Team members" subtitle="People who use a seat on the studio's plan" p="lg">
              <TeamTable rows={members} />
            </SectionCard>
            <SectionCard title="Clients" subtitle="Invited to view their projects — don't use a seat" p="lg">
              <TeamTable rows={clients} />
            </SectionCard>
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="projects">
          <ProjectsTab data={data} />
        </Tabs.Panel>
        <Tabs.Panel value="billing">
          <BillingTab data={data} orgId={id} />
        </Tabs.Panel>
        <Tabs.Panel value="plan">
          <PlanTab orgId={id} onChanged={reload} />
        </Tabs.Panel>
      </Tabs>

      <Text size="xs" c="dimmed">
        Updated {fmtDateTime(data.generatedAt)} ·{' '}
        <Anchor size="xs" onClick={() => nav('/studios')}>
          all studios
        </Anchor>
      </Text>
    </Stack>
  );
}

function BackLink() {
  return (
    <Button component={Link} to="/studios" variant="subtle" leftSection={<IconArrowLeft size={14} />} px={6}>
      Studios
    </Button>
  );
}

function MiniStat({ label: l, value }: { label: string; value: ReactNode }) {
  return (
    <Box p="sm" style={{ background: '#F8FAFC', borderRadius: 10 }}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: 0.4 }}>
        {l}
      </Text>
      <Text fw={700} size="lg">
        {value}
      </Text>
    </Box>
  );
}

/** Label / value row; renders an em-dash for missing values. */
function KV({ k, v, icon }: { k: string; v: ReactNode; icon?: ReactNode }) {
  const empty = v === null || v === undefined || v === '';
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md" py={6}>
      <Group gap={6} wrap="nowrap" c="dimmed">
        {icon}
        <Text size="sm" c="dimmed">
          {k}
        </Text>
      </Group>
      <Box ta="right" style={{ minWidth: 0 }}>
        {empty ? (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ) : typeof v === 'string' || typeof v === 'number' ? (
          <Text size="sm" fw={500} style={{ wordBreak: 'break-word' }}>
            {v}
          </Text>
        ) : (
          v
        )}
      </Box>
    </Group>
  );
}

function ContactLinks({ phone }: { phone: string | null }) {
  if (!phone) return <Text size="sm">—</Text>;
  return (
    <Group gap={6} justify="flex-end" wrap="nowrap">
      <Text size="sm" fw={500}>
        {fmtPhone(phone)}
      </Text>
      <Tooltip label="Call">
        <ActionIcon component="a" href={`tel:${phone}`} size="sm" variant="light">
          <IconPhone size={14} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="WhatsApp">
        <ActionIcon component="a" href={waLink(phone) ?? undefined} target="_blank" rel="noreferrer" size="sm" variant="light" color="green">
          <IconBrandWhatsapp size={14} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

function ext(url: string | null, text?: string): ReactNode {
  if (!url) return null;
  const href = /^https?:\/\//.test(url) ? url : `https://${url}`;
  return (
    <Anchor href={href} target="_blank" rel="noreferrer" size="sm">
      {text ?? url} <IconExternalLink size={12} style={{ verticalAlign: -1 }} />
    </Anchor>
  );
}

// ── Overview ───────────────────────────────────────────────────────

function OverviewTab({
  data,
  onOpenTeam,
  onRecalculated,
}: {
  data: StudioDetail;
  onOpenTeam: () => void;
  onRecalculated: () => void;
}) {
  const p = data.profile;
  const s = data.studio;
  const sub = data.subscription;
  const owner = data.team.find((m) => m.isOwner) ?? null;
  const address = [p.addressLine1, p.addressLine2, [p.city, p.state, p.pincode].filter(Boolean).join(', '), p.country]
    .filter(Boolean)
    .join('\n');

  return (
    <Grid gutter="lg">
      <Grid.Col span={{ base: 12, md: 6, xl: 4 }}>
        <SectionCard title="Owner" subtitle="Studio owner (Super Admin)">
          {owner ? (
            <Stack gap={0}>
              <KV k="Name" v={owner.name} />
              <KV k="Mobile" v={<ContactLinks phone={owner.phone} />} />
              <KV k="Email" v={owner.email ? <Anchor href={`mailto:${owner.email}`} size="sm">{owner.email}</Anchor> : null} />
              <Divider my="xs" />
              <KV k="Signed up" v={fmtDate(owner.createdAt)} />
              <KV k="Sign-up platform" v={<PlatformBadge platform={owner.signupPlatform} />} />
              <KV
                k="Last active"
                v={
                  <Stack gap={2} align="flex-end">
                    <RelTime ms={owner.lastActiveAt} />
                    {owner.lastPlatform !== 'unknown' ? <PlatformBadge platform={owner.lastPlatform} /> : null}
                  </Stack>
                }
              />
              <KV
                k="Device"
                v={
                  owner.deviceModel || owner.appVersion
                    ? [owner.deviceModel, owner.osVersion ? `OS ${owner.osVersion}` : null, owner.appVersion ? `app v${owner.appVersion}` : null]
                        .filter(Boolean)
                        .join(' · ')
                    : null
                }
              />
              <KV k="Push notifications" v={owner.pushEnabled ? 'On' : 'Off / not set up'} />
              {owner.studios.length > 1 ? (
                <KV
                  k="Also in"
                  v={
                    <Stack gap={2} align="flex-end">
                      {owner.studios
                        .filter((st) => st.id !== s.id)
                        .map((st) => (
                          <Anchor key={st.id} component={Link} to={`/studios/${st.id}`} size="sm">
                            {st.name} · {label(ROLE_LABEL, st.role)}
                          </Anchor>
                        ))}
                    </Stack>
                  }
                />
              ) : null}
              <Button variant="light" size="xs" mt="sm" onClick={onOpenTeam}>
                View whole team
              </Button>
            </Stack>
          ) : (
            <EmptyState title="Owner account not found" />
          )}
        </SectionCard>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6, xl: 4 }}>
        <SectionCard title="Studio profile" subtitle={p.profileUpdatedAt ? `Updated ${fmtDate(p.profileUpdatedAt)}` : 'Filled in by the studio'}>
          <Stack gap={0}>
            <KV
              k="Address"
              icon={<IconMapPin size={14} />}
              v={address ? <Text size="sm" fw={500} style={{ whiteSpace: 'pre-line' }}>{address}</Text> : null}
            />
            <KV k="Studio email" icon={<IconMail size={14} />} v={p.email ? <Anchor href={`mailto:${p.email}`} size="sm">{p.email}</Anchor> : null} />
            <KV k="Alt. email" v={p.altEmail} />
            <KV k="Alt. phone" v={p.altPhone ? <ContactLinks phone={p.altPhone} /> : null} />
            <KV k="Website" icon={<IconWorldWww size={14} />} v={ext(p.website)} />
            <KV k="Instagram" icon={<IconBrandInstagram size={14} />} v={ext(p.instagram, 'Open profile')} />
            <KV k="LinkedIn" icon={<IconBrandLinkedin size={14} />} v={ext(p.linkedin, 'Open profile')} />
            <KV k="Founded" v={p.founded} />
          </Stack>
        </SectionCard>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6, xl: 4 }}>
        <SectionCard title="Business & tax" subtitle="Used on the studio's invoices and quotations">
          <Stack gap={0}>
            <KV k="GSTIN" v={p.gstin ? <CopyText value={p.gstin} /> : null} />
            <KV k="PAN" v={p.pan} />
            <KV k="RERA" v={p.rera} />
            <Divider my="xs" />
            <KV k="Bank" icon={<IconBuildingBank size={14} />} v={p.bankName} />
            <KV k="Account" v={p.bankAccount} />
            <KV k="IFSC" v={p.bankIFSC} />
            <KV k="Branch" v={p.bankBranch} />
            <KV k="UPI" v={p.upi} />
          </Stack>
        </SectionCard>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6, xl: 8 }}>
        <SectionCard title="Subscription" subtitle="Current plan and where it's billed">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
            <Stack gap={0}>
              <KV k="Plan" v={<TierBadge tier={s.effectiveTier} />} />
              <KV k="Status" v={<StatusBadge status={sub.status} />} />
              <KV k="Billed through" v={<ChannelBadge channel={s.channel} sandbox={sub.environment === 'SANDBOX'} />} />
              <KV k="Product" v={sub.productId ? <Text size="sm" ff="monospace">{sub.productId}</Text> : null} />
              <KV k="Billing period" v={sub.period ? sub.period[0].toUpperCase() + sub.period.slice(1) : null} />
            </Stack>
            <Stack gap={0}>
              <KV k={sub.willRenew ? 'Renews on' : 'Ends on'} v={sub.expiresAt ? fmtDate(sub.expiresAt) : 'No end date'} />
              <KV k="Auto-renew" v={s.channel === 'free' ? null : sub.willRenew ? 'On' : 'Off'} />
              <KV k="Last change" v={fmtDateTime(sub.updatedAt)} />
              <KV k="Razorpay subscription" v={sub.razorpaySubscriptionId ? <CopyText value={sub.razorpaySubscriptionId} /> : null} />
              <KV k="Stored plan (before expiry)" v={label(TIER_LABEL, sub.tier)} />
            </Stack>
          </SimpleGrid>
          {sub.manualNote ? (
            <Alert mt="md" color="grape" variant="light" radius="md" icon={<IconShieldHalfFilled size={16} />}>
              <Text size="sm">
                Set from admin{sub.manualAt ? ` on ${fmtDate(sub.manualAt)}` : ''}: <i>{sub.manualNote}</i>
              </Text>
            </Alert>
          ) : null}
        </SectionCard>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6, xl: 4 }}>
        <SectionCard title="Plan limits" subtitle="7-day grace, then paused seats / view-only projects">
          <Stack gap="sm">
            {s.seatGraceEndsAt ? (
              <Alert color={s.seatsPaused ? 'orange' : 'yellow'} variant="light" radius="md" icon={<IconPlayerPause size={16} />}>
                <Text size="sm">
                  {s.seatsPaused
                    ? `${s.seatsPaused} member(s) paused since ${fmtDate(s.seatGraceEndsAt)}.`
                    : `More members than the plan allows — extra members pause on ${fmtDate(s.seatGraceEndsAt)}.`}
                </Text>
              </Alert>
            ) : null}
            {s.projectGraceEndsAt ? (
              <Alert color={s.projectsLocked ? 'orange' : 'yellow'} variant="light" radius="md" icon={<IconLock size={16} />}>
                <Text size="sm">
                  {s.projectsLocked
                    ? `${s.projectsLocked} older project(s) view-only since ${fmtDate(s.projectGraceEndsAt)}.`
                    : `More projects than the plan allows — older projects become view-only on ${fmtDate(s.projectGraceEndsAt)}.`}
                </Text>
              </Alert>
            ) : null}
            {!s.seatGraceEndsAt && !s.projectGraceEndsAt ? (
              <Text size="sm" c="dimmed">
                Within plan limits.
              </Text>
            ) : null}
          </Stack>
        </SectionCard>
      </Grid.Col>

      <Grid.Col span={12}>
        <StorageCard data={data} onRecalculated={onRecalculated} />
      </Grid.Col>
    </Grid>
  );
}

function StorageCard({ data, onRecalculated }: { data: StudioDetail; onRecalculated: () => void }) {
  const s = data.studio;
  const kinds = Object.entries(s.storageByKind).sort((a, b) => b[1] - a[1]);
  const projects = data.projects.filter((p) => p.storageBytes > 0).sort((a, b) => b.storageBytes - a.storageBytes).slice(0, 8);
  return (
    <SectionCard
      title="Storage"
      subtitle={
        (data.storageRecalculatedAt ? `Recounted ${fmtDateTime(data.storageRecalculatedAt)} · ` : '')
        + "Every team member's uploads count toward the studio · limit tracked, not enforced"
      }
      action={<RecalculateStorageButton onDone={onRecalculated} />}
    >
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="sm">
            <StorageBar used={s.storageBytes} limit={s.storageLimitBytes} />
            <Text size="sm" c="dimmed">
              {s.storageFiles.toLocaleString('en-IN')} file{s.storageFiles === 1 ? '' : 's'} · {label(TIER_LABEL, s.effectiveTier)} plan
              {s.storageLimitBytes ? ` includes ${fmtBytes(s.storageLimitBytes)}` : ''}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs" style={{ letterSpacing: 0.4 }}>
            By type
          </Text>
          {kinds.length === 0 ? (
            <Text size="sm" c="dimmed">
              No files yet
            </Text>
          ) : (
            <Stack gap={6}>
              {kinds.map(([k, bytes]) => (
                <Group key={k} justify="space-between" wrap="nowrap">
                  <Text size="sm">{STORAGE_KIND_LABEL[k] ?? k}</Text>
                  <Text size="sm" fw={600}>
                    {fmtBytes(bytes)}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs" style={{ letterSpacing: 0.4 }}>
            Largest projects
          </Text>
          {projects.length === 0 ? (
            <Text size="sm" c="dimmed">
              No project files yet
            </Text>
          ) : (
            <Stack gap={6}>
              {projects.map((p) => (
                <Group key={p.id} justify="space-between" wrap="nowrap">
                  <Text size="sm" truncate>
                    {p.name}
                  </Text>
                  <Text size="sm" fw={600} style={{ flexShrink: 0 }}>
                    {fmtBytes(p.storageBytes)}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
        </Grid.Col>
      </Grid>
    </SectionCard>
  );
}

// ── Team ───────────────────────────────────────────────────────────

function TeamTable({ rows }: { rows: TeamMember[] }) {
  if (rows.length === 0) return <EmptyState title="Nobody here yet" />;
  return (
    <Table.ScrollContainer minWidth={900}>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Person</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Projects</Table.Th>
            <Table.Th>Signed up</Table.Th>
            <Table.Th>Last active</Table.Th>
            <Table.Th>Device</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((m) => (
            <Table.Tr key={m.uid}>
              <Table.Td>
                <Stack gap={2}>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={600}>
                      {m.name ?? fmtPhone(m.phone)}
                    </Text>
                    {m.paused ? (
                      <Badge size="xs" color="orange" variant="light" leftSection={<IconPlayerPause size={10} />}>
                        Paused
                      </Badge>
                    ) : null}
                  </Group>
                  <Group gap={6} wrap="nowrap">
                    {m.name ? <Text size="xs" c="dimmed">{fmtPhone(m.phone)}</Text> : null}
                    {m.phone ? (
                      <Anchor href={waLink(m.phone) ?? undefined} target="_blank" rel="noreferrer" c="green" style={{ display: 'flex' }}>
                        <IconBrandWhatsapp size={13} />
                      </Anchor>
                    ) : null}
                    {m.email ? <Text size="xs" c="dimmed">{m.email}</Text> : null}
                  </Group>
                </Stack>
              </Table.Td>
              <Table.Td>
                <Badge variant={m.isOwner ? 'filled' : 'light'} color={m.role === 'client' ? 'gray' : 'brand'} radius="sm">
                  {label(ROLE_LABEL, m.role)}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{m.role === 'superAdmin' || m.role === 'admin' ? 'All' : m.projectCount}</Text>
              </Table.Td>
              <Table.Td>
                <Stack gap={4}>
                  <Text size="sm">{fmtDate(m.createdAt)}</Text>
                  <PlatformBadge platform={m.signupPlatform} />
                </Stack>
              </Table.Td>
              <Table.Td>
                <Stack gap={4}>
                  <RelTime ms={m.lastActiveAt} />
                  {m.lastPlatform !== 'unknown' ? <PlatformBadge platform={m.lastPlatform} /> : null}
                </Stack>
              </Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">
                  {[m.deviceModel, m.osVersion ? `OS ${m.osVersion}` : null, m.appVersion ? `v${m.appVersion}` : null]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

// ── Projects ───────────────────────────────────────────────────────

const PROJECT_STATUS: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: 'teal' },
  on_hold: { label: 'On hold', color: 'yellow' },
  completed: { label: 'Completed', color: 'gray' },
};

function ProjectsTab({ data }: { data: StudioDetail }) {
  const total = data.projects.reduce((n, p) => n + (p.value ?? 0), 0);
  return (
    <SectionCard
      title="Projects"
      subtitle={total > 0 ? `Total project value ${fmtInr(total)}` : undefined}
      p="lg"
    >
      {data.projects.length === 0 ? (
        <EmptyState title="No projects yet" />
      ) : (
        <Table.ScrollContainer minWidth={900}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Project</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Progress</Table.Th>
                <Table.Th>Value</Table.Th>
                <Table.Th>Storage</Table.Th>
                <Table.Th>People</Table.Th>
                <Table.Th>Created</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.projects.map((p) => {
                const st = PROJECT_STATUS[p.status ?? ''] ?? { label: p.status ?? '—', color: 'gray' };
                return (
                  <Table.Tr key={p.id}>
                    <Table.Td>
                      <Stack gap={2}>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm" fw={600}>
                            {p.name}
                          </Text>
                          {p.locked ? (
                            <Badge size="xs" color="orange" variant="light" leftSection={<IconLock size={10} />}>
                              View-only
                            </Badge>
                          ) : null}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {[p.client, p.location].filter(Boolean).join(' · ') || '—'}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        <Badge size="sm" color={st.color} variant="light" radius="sm">
                          {st.label}
                        </Badge>
                        {p.phase ? (
                          <Text size="xs" c="dimmed" tt="capitalize">
                            {p.phase.replace(/_/g, ' ')}
                          </Text>
                        ) : null}
                      </Stack>
                    </Table.Td>
                    <Table.Td w={140}>
                      {p.progress !== null ? (
                        <Stack gap={2}>
                          <Text size="xs">{Math.round(p.progress)}%</Text>
                          <Progress value={p.progress} size="sm" radius="xl" />
                        </Stack>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{p.value ? fmtInr(p.value) : '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{p.storageBytes ? fmtBytes(p.storageBytes) : '—'}</Text>
                      {p.storageFiles ? (
                        <Text size="xs" c="dimmed">
                          {p.storageFiles} file{p.storageFiles === 1 ? '' : 's'}
                        </Text>
                      ) : null}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {p.memberCount} team · {p.clientCount} client{p.clientCount === 1 ? '' : 's'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{fmtDate(p.createdAt)}</Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </SectionCard>
  );
}

// ── Billing ────────────────────────────────────────────────────────

function BillingTab({ data, orgId }: { data: StudioDetail; orgId: string }) {
  const storeEvents = data.billing.filter((e) => e.kind === 'store');
  return (
    <Stack gap="lg">
      <SectionCard title="App Store & Google Play events" subtitle="From RevenueCat — purchases, renewals, cancellations" p="lg">
        {storeEvents.length === 0 ? (
          <EmptyState title="No store events for this studio" />
        ) : (
          <Table.ScrollContainer minWidth={800}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Event</Table.Th>
                  <Table.Th>Plan</Table.Th>
                  <Table.Th>Store</Table.Th>
                  <Table.Th>Amount</Table.Th>
                  <Table.Th>Note</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {storeEvents.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td>
                      <Text size="sm">{fmtDateTime(e.at)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {label(EVENT_LABEL, e.type)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {e.tier ? `${label(TIER_LABEL, e.tier)}${e.period ? ` · ${e.period}` : ''}` : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Badge size="sm" variant="light" color={e.store === 'play_store' ? 'green' : 'dark'}>
                          {label(STORE_LABEL, e.store)}
                        </Badge>
                        {e.environment === 'SANDBOX' ? (
                          <Badge size="sm" variant="light" color="yellow">
                            Test
                          </Badge>
                        ) : null}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{fmtMoney(e.amount, e.currency)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c={e.warning ? 'red' : 'dimmed'}>
                        {e.warning ?? ''}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <WebPayments orgId={orgId} />
    </Stack>
  );
}

function WebPayments({ orgId }: { orgId: string }) {
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refundTarget, setRefundTarget] = useState<PaymentRow | null>(null);

  useEffect(() => {
    setLoading(true);
    return onSnapshot(
      query(collection(db, 'organizations', orgId, 'payments'), orderBy('capturedAt', 'desc'), limit(25)),
      (snap) => {
        setPayments(snap.docs.map((d) => ({ ...(d.data() as PaymentRow), id: d.id })));
        setLoading(false);
      },
      (err) => {
        console.warn('[OrgDetail] payments snapshot error', err);
        setLoading(false);
      },
    );
  }, [orgId]);

  return (
    <SectionCard title="Web payments (Razorpay)" subtitle="Legacy web billing — invoices and refunds" p="lg">
      {loading ? (
        <LoadingBlock />
      ) : payments.length === 0 ? (
        <EmptyState title="No web payments for this studio" />
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th>Plan</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Invoice</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Action</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {payments.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td>{p.capturedAt ? fmtDate(p.capturedAt.toMillis()) : '—'}</Table.Td>
                  <Table.Td>{fmtPaise(p.amount ?? 0)}</Table.Td>
                  <Table.Td>
                    {label(TIER_LABEL, p.tier)}
                    {p.period ? ` · ${p.period}` : ''}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="dot" color={p.status === 'captured' ? 'teal' : 'gray'}>
                      {p.status ?? 'unknown'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {p.invoiceUrl ? (
                      <Anchor href={p.invoiceUrl} target="_blank" rel="noreferrer" size="sm">
                        Open PDF
                      </Anchor>
                    ) : (
                      '—'
                    )}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {p.refund ? (
                      <Text size="xs" c="dimmed">
                        Refunded {fmtPaise(p.refund.amount ?? 0)}
                        {p.refund.refundedAt ? ` on ${fmtDate(p.refund.refundedAt.toMillis())}` : ''}
                      </Text>
                    ) : (
                      <Button
                        size="xs"
                        color="red"
                        variant="light"
                        leftSection={<IconReceiptRefund size={14} />}
                        onClick={() => setRefundTarget(p)}
                        disabled={p.status !== 'captured'}
                      >
                        Refund
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <RefundModal target={refundTarget} orgId={orgId} onClose={() => setRefundTarget(null)} />
    </SectionCard>
  );
}

// ── Manage plan (override) ─────────────────────────────────────────

function PlanTab({ orgId, onChanged }: { orgId: string; onChanged: () => void }) {
  const [org, setOrg] = useState<OrgDoc | null>(null);
  const [tier, setTier] = useState<Tier>('studio');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(
    () =>
      onSnapshot(doc(db, 'organizations', orgId), (snap) => {
        const d = (snap.data() ?? {}) as OrgDoc;
        setOrg(d);
        const t = d.subscription?.tier;
        if (t === 'free' || t === 'solo' || t === 'studio' || t === 'studio_plus' || t === 'agency') setTier(t);
        const exp = d.subscription?.expiresAt;
        setExpiresAt(exp ? new Date(exp.toMillis()) : null);
      }),
    [orgId],
  );

  const apply = async (nextTier: Tier, nextExpires: Date | null, nextNote: string | undefined) => {
    setSaving(true);
    try {
      const res = await callAdmin.overrideOrgTier({
        orgId,
        tier: nextTier,
        expiresAt: nextExpires ? nextExpires.toISOString() : null,
        note: nextNote,
      });
      const r = res.data;
      notifications.show(
        r.razorpayCancelFailed
          ? {
              title: 'Plan changed — Razorpay cancel failed',
              message: `${r.razorpayCancelError ?? 'Unknown error'}. Cancel the web subscription in the Razorpay dashboard.`,
              color: 'orange',
              autoClose: 10000,
            }
          : { title: 'Plan updated', message: 'Live for the studio now. Recorded in the audit log.', color: 'green' },
      );
      setNote('');
      onChanged();
    } catch (err) {
      notifications.show({ title: 'Update failed', message: (err as Error).message, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const isManual = org?.subscription?.source === 'manual';

  return (
    <Grid gutter="lg">
      <Grid.Col span={{ base: 12, md: 7 }}>
        <SectionCard title="Set plan manually" subtitle="Comp accounts, trial extensions, support fixes">
          <Stack gap="md">
            {isManual ? (
              <Alert icon={<IconShieldHalfFilled size={16} />} color="grape" variant="light" radius="md">
                <Text size="sm">
                  This studio is on a <b>plan set from admin</b>.
                  {org?.subscription?.manualOverrideNote ? (
                    <>
                      {' '}
                      Note: <i>{org.subscription.manualOverrideNote}</i>
                    </>
                  ) : null}
                </Text>
              </Alert>
            ) : null}
            <Group gap="md" align="flex-end">
              <Select
                label="Plan"
                value={tier}
                onChange={(v) => v && setTier(v as Tier)}
                data={Object.entries(TIER_LABEL).map(([value, l]) => ({ value, label: l }))}
                w={170}
                allowDeselect={false}
              />
              <DateInput
                label="Ends on"
                description="Leave empty for no end date"
                placeholder="No end date"
                value={expiresAt}
                onChange={(v) => setExpiresAt(typeof v === 'string' ? (v ? new Date(v) : null) : v)}
                clearable
                w={220}
                minDate={new Date()}
              />
            </Group>
            <Textarea
              label="Note (saved to the audit log)"
              placeholder="e.g. 30-day Studio trial for design partner"
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              autosize
              minRows={2}
            />
            <Group>
              <Button onClick={() => apply(tier, expiresAt, note.trim() || undefined)} loading={saving}>
                Apply plan
              </Button>
              <Button
                color="red"
                variant="light"
                loading={saving}
                onClick={() => apply('free', null, 'Reset to Free via admin (manual override)')}
              >
                Reset to Free
              </Button>
            </Group>
          </Stack>
        </SectionCard>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 5 }}>
        <SectionCard title="Before you change a plan">
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              • Studios that pay through the App Store or Google Play manage their subscription in the store —
              a manual plan here is overwritten by their next store renewal or cancellation.
            </Text>
            <Text size="sm" c="dimmed">
              • Resetting a web (Razorpay) studio to Free also cancels its Razorpay subscription.
            </Text>
            <Text size="sm" c="dimmed">
              • Dropping to a smaller plan starts the 7-day grace period for extra members / projects.
            </Text>
          </Stack>
        </SectionCard>
      </Grid.Col>
    </Grid>
  );
}

// ── Refund modal (Razorpay) ────────────────────────────────────────

function RefundModal({
  target,
  orgId,
  onClose,
}: {
  target: PaymentRow | null;
  orgId: string;
  onClose: () => void;
}) {
  const originalPaise = target?.amount ?? 0;
  const [amountInr, setAmountInr] = useState<number | string>(originalPaise / 100);
  const [reason, setReason] = useState('');
  const [alsoCancel, setAlsoCancel] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setAmountInr(originalPaise / 100);
    setReason('');
    setAlsoCancel(false);
  }, [target, originalPaise]);

  const onSubmit = async () => {
    if (!target) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      notifications.show({ title: 'Reason required', message: 'Add a short reason — it goes into the audit log.', color: 'red' });
      return;
    }
    const paise = Math.round(Number(amountInr) * 100);
    if (!Number.isFinite(paise) || paise <= 0) {
      notifications.show({ title: 'Invalid amount', message: 'Refund amount must be a positive number.', color: 'red' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await callAdmin.refundRazorpayPayment({
        orgId,
        paymentId: target.paymentId ?? target.id,
        amountPaise: paise === originalPaise ? null : paise,
        reason: trimmed,
        alsoCancelSubscription: alsoCancel,
      });
      const d = res.data;
      let msg = `Refund ${d.refundId} created (${d.refundStatus}). ${fmtPaise(d.refundedAmountPaise)} returns to the customer in 5–7 business days.`;
      if (alsoCancel) {
        msg += d.subscriptionCancelFailed
          ? ` Subscription cancel FAILED: ${d.subscriptionCancelError ?? 'unknown'}. Cancel it in Razorpay.`
          : d.subscriptionCancelled
            ? ' Subscription also cancelled in Razorpay.'
            : '';
      }
      notifications.show({
        title: 'Refund processed',
        message: msg,
        color: d.subscriptionCancelFailed ? 'orange' : 'green',
        autoClose: 10000,
      });
      onClose();
    } catch (err) {
      notifications.show({ title: 'Refund failed', message: (err as Error).message, color: 'red', autoClose: 10000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={Boolean(target)}
      onClose={() => !submitting && onClose()}
      title={target ? `Refund payment ${target.paymentId ?? target.id}` : 'Refund payment'}
      centered
      size="md"
    >
      {target ? (
        <Stack gap="md">
          <Alert color="yellow" variant="light">
            <Text size="sm">
              Refunds go back to the customer's original payment method through Razorpay (5–7 business days).
              This is logged in the audit log and can't be undone here.
            </Text>
          </Alert>
          <NumberInput
            label="Refund amount (₹)"
            description={`Original payment: ${fmtPaise(originalPaise)}`}
            value={amountInr}
            onChange={setAmountInr}
            min={1}
            max={originalPaise / 100}
            decimalScale={2}
            thousandSeparator=","
            required
          />
          <Textarea
            label="Reason"
            placeholder="e.g. Charged twice after a UPI retry"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={500}
            required
          />
          <Checkbox
            label="Also cancel the studio's Razorpay subscription"
            description="Cancels immediately — use when this refund is part of a full cancellation."
            checked={alsoCancel}
            onChange={(e) => setAlsoCancel(e.currentTarget.checked)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button color="red" onClick={onSubmit} loading={submitting}>
              Issue refund
            </Button>
          </Group>
        </Stack>
      ) : null}
    </Modal>
  );
}

function fmtPaise(paise: number): string {
  return fmtMoney(paise / 100, 'INR');
}
